/**
 * Canonical machine-API endpoints, shared by every tool-setup surface
 * (Claude Code, Codex, Cowork, Copilot) and the raw base-URL displays.
 *
 * All setups intentionally target production regardless of where a key was
 * minted — customers configure their tools against prod, never a dev or UAT
 * origin. api.* is the canonical machine-API host (Google LB → api-gateway →
 * web): auth + wallet metering happen at the gateway edge. portal.* still
 * accepts API traffic as a legacy fallback but new setups should not be
 * pointed at it.
 */
export const PROD_BASE_URL = "https://api.aireserve.com";

/** Env var the manual (shell) setup flows read the API key from at runtime. */
export const ENV_KEY = "AIRESERVE_API_KEY";

/** Anthropic Messages wire version the gateway speaks (verification pings, curl examples). */
export const ANTHROPIC_API_VERSION = "2023-06-01";

/**
 * Identity our gateway takes inside third-party tool configs (e.g. the Zed
 * provider id). Chosen so tools that derive an env var from it (uppercase +
 * `_API_KEY`) land exactly on ENV_KEY. Codex shares the spelling since the
 * 2026-09 rename (harness-config CODEX_PROVIDER_ID); its connect flow still
 * find-and-replaces the pre-rename `[model_providers.audacity]` block so old
 * machines migrate instead of ending up with two providers.
 */
export const GATEWAY_PROVIDER_ID = "aireserve";

/** Public developer documentation, linked from every setup surface. */
export const DEVELOPER_DOCS_URL = "https://docs.aireserve.com";

/**
 * Wire-compatible endpoint suffixes on the machine API, relative to a base
 * URL. Single source for the surfaces we tell developers (and their agents,
 * via the MCP connection guide) to point tools at.
 */
export const MACHINE_API_ENDPOINTS = {
  openaiChatCompletions: "/v1/chat/completions",
  openaiResponses: "/v1/responses",
  anthropicMessages: "/v1/messages",
  bedrockConverse: "/model/{modelId}/converse",
  listModels: "/v1/models",
} as const;

/** Guided per-tool connect pages, relative to the portal origin. */
export const CONNECT_PAGE_PATHS = {
  claudeCode: "/quickstart/coding-agents/claude-code",
  codex: "/quickstart/coding-agents/codex",
  cursor: "/quickstart/coding-agents/cursor",
  copilot: "/quickstart/coding-agents/copilot",
} as const;
